import { Button, ColumnLayout } from 'AWS-UI-Components-React';
import React from 'react';
import { ASIN_METRIC_TYPE, COMPONENT_NAMES, TREND_CHART_DROPDOWN_ELEMENTS } from '../../../constants/asin_metric_elements';
import { CartesianGrid, ComposedChart, Label, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from '../../../recharts';
import { AccuracyButtonNewStyle, ASINChartStyle } from '../../../styles/common_styles';
import { downloadFile, toDownloadFormat } from '../../../utilities';
import { formatTimeFrameToYYYYMM, formatYYYYMMtoTimeFrame, YYYYMMDDToDate } from '../../../utils/date-utils';
import { CustomizedDot, getMaximallyDifferentRandomColor } from '../../../utils/graph-utils';
import { TrendFilter } from '../../buttons/Trend_Filter';
import { EmptyMessage } from '../../content/empty_message';
import { LoadingSpinner } from '../../content/loading_spinner';
import { OPERATION_STATUS } from '../common/constants';
import { extractMetric, filterLinesOrBars, generateLabel, getColumnDefinitions } from './asin_metrics_utils';
import { CompareMonthWindow } from './compare_month_window';

export class ASINTrendChart extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedComponents: this.props.component ? [this.props.component] : ['contributionProfit', 'contributionProfitWithoutRefunds'],
      selectedMetrics: [ASIN_METRIC_TYPE.ABS_VAR_PER_UNIT],
      selectedMonths: this.props.availableMonths,
      compareStatus: OPERATION_STATUS.NONE,
      downloadStatus: OPERATION_STATUS.NONE,
    };
  };

  componentDidUpdate = (prevProps) => {
    if (prevProps.availableMonths !== this.props.availableMonths) {
      const selectedMonths = [...this.state.selectedMonths, ...this.props.availableMonths];
      this.setState({selectedMonths});
    }
  };

  getLines = (allLineIds, selectedComponents, selectedMetrics, selectedMonths) => {
    let lines = [];
    let colorIndex = 0;
    const filteredLines = filterLinesOrBars(allLineIds, selectedComponents, selectedMetrics, selectedMonths);
    filteredLines.forEach((id) => {
      lines.push(
        <Line
          name={generateLabel(id, selectedMonths.length > 1, selectedMetrics.length > 1)}
          strokeWidth="2"
          key={id}
          dataKey={id}
          dot={<CustomizedDot />}
          stroke={getMaximallyDifferentRandomColor(colorIndex++, filteredLines.length)} />);
    });
    return lines;
  };

  getComponentDropdownElements = () => {
    return Object.keys(COMPONENT_NAMES).map(comp => { return {id: comp, label:COMPONENT_NAMES[comp]};});
  };

  setSelectedMetrics = (chosen) => {
    this.setState({selectedMetrics: chosen.detail.selectedIds})
  };

  setSelectedComponents = (chosen) => {
    this.setState({selectedComponents: chosen.detail.selectedIds})
  };

  setSelectedMonths = (chosen) => {
    this.setState({selectedMonths: chosen.detail.selectedIds})
  };


  getFormattedChartData = (data, displayInMillions) => {
    let newData = [];
    for(const snapshotRow of data){
      let newSnapshotRow = Object.assign({}, snapshotRow);
      for(const key of Object.keys(snapshotRow)){
        if(key !== 'snapshot'){
          const metric = extractMetric(key);
          newSnapshotRow[key] = this.getFormattedValue(snapshotRow[key], metric, displayInMillions);
        }
      }
      newData.push(newSnapshotRow);
    }
    return newData;
  };

  getFormattedValue = (value, metric, displayInMillions) => {
    let formattedValue;
    if([ASIN_METRIC_TYPE.NET_VAR_PER_UNIT,
        ASIN_METRIC_TYPE.ABS_VAR_PER_UNIT,
        ASIN_METRIC_TYPE.WINSORIZED_ABS_VAR_PER_UNIT,
        ASIN_METRIC_TYPE.WINSORIZED_NET_VAR_PER_UNIT].includes(metric)){
      formattedValue = value;
    } else {
      formattedValue = displayInMillions ? value/1000000: value;
    }
    return formattedValue.toFixed(2);
  };

  getMonthsDropdownElements = () => {
    return this.props.availableMonths.map(m => {return {id: m, label: m};});
  };

  onCompareMonth = () => {
    this.setState({compareStatus: OPERATION_STATUS.IN_PROGRESS});
  };

  onCancelCompare = () => {
    this.setState({compareStatus: OPERATION_STATUS.NONE});
  };

  onSubmitCompare = (timeFrames) => {
    this.setState({compareStatus: OPERATION_STATUS.SUCESS});
    this.props.onCompareMonths(timeFrames);
  };

  /**
   * Exclude months that have already been pulled from Compare Months dropdown
   */
  getExcludedTimeFrames = () => {
    return this.props.availableMonths.map(m => formatYYYYMMtoTimeFrame(m));
  };

  getDataMinandMax = (allLineIds, selectedComponents, selectedMetrics, selectedMonths) => {
    const selectedLines = filterLinesOrBars(allLineIds, selectedComponents, selectedMetrics, selectedMonths);
    const {data, displayInMillions} = this.props;
    const filteredData = data.map(snapshot => Object.keys(snapshot)
                                                    .filter(key => selectedLines.includes(key))
                                                    .map(key => this.getFormattedValue(snapshot[key],
                                                                                       extractMetric(key), displayInMillions)))
                             .flat();
    const dataMin = filteredData.length > 0 ? Math.min(...filteredData) : 0;
    const dataMax = filteredData.length > 0 ? Math.max(...filteredData) : 0;
    return [dataMin, dataMax];
  }

  /** Convert snapshotToComponentMetricsMap (snapshot => Map(component => Map(metric=> metric value)))
   * to desired excel download format.
   */
  onDownloadData = () => {
    const {snapshotToComponentMetricMap, displayInMillions, payload} = this.props;
    this.setState({downloadStatus: OPERATION_STATUS.IN_PROGRESS});
    const month = formatTimeFrameToYYYYMM(payload.timeFrame);
    let dataRows = [];
    for (const [snapshot, componentToMetricsMap] of snapshotToComponentMetricMap.entries()){
      for (const [component, metricsMap] of componentToMetricsMap.entries()){
        const componentName = COMPONENT_NAMES[component] || component;
        dataRows.push({component: componentName,
                        snapshot: snapshot,
                        month: month,
                        [ASIN_METRIC_TYPE.NET_FA]: metricsMap.get(ASIN_METRIC_TYPE.NET_FA),
                        [ASIN_METRIC_TYPE.NET_LA]: metricsMap.get(ASIN_METRIC_TYPE.NET_LA),
                        [ASIN_METRIC_TYPE.FA_UNITS]: metricsMap.get(ASIN_METRIC_TYPE.FA_UNITS),
                        [ASIN_METRIC_TYPE.LA_UNITS]: metricsMap.get(ASIN_METRIC_TYPE.LA_UNITS),
                        [ASIN_METRIC_TYPE.NET_VAR_PER_UNIT]: metricsMap.get(ASIN_METRIC_TYPE.NET_VAR_PER_UNIT),
                        [ASIN_METRIC_TYPE.ABS_VAR_PER_UNIT]: metricsMap.get(ASIN_METRIC_TYPE.ABS_VAR_PER_UNIT),
                        [ASIN_METRIC_TYPE.WINSORIZED_NET_VAR_PER_UNIT]: metricsMap.get(ASIN_METRIC_TYPE.WINSORIZED_NET_VAR_PER_UNIT),
                        [ASIN_METRIC_TYPE.WINSORIZED_ABS_VAR_PER_UNIT]: metricsMap.get(ASIN_METRIC_TYPE.WINSORIZED_ABS_VAR_PER_UNIT)
                      });
      }
    }
    const data = toDownloadFormat(getColumnDefinitions(dataRows, displayInMillions), dataRows);
    downloadFile(data, `GL_Metrics_Trend_${month}.xls`);
    this.setState({downloadStatus: OPERATION_STATUS.SUCESS});
  };


  render() {
    const {data, lines, displayInMillions, loading} = this.props;
    const componentOpts = this.getComponentDropdownElements();
    const monthOpts = this.getMonthsDropdownElements(data);
    const empty = !data || !data.length;
    const compareMonths = this.props.hasOwnProperty('compareMonths') ? this.props.compareMonths : true;
    const [dataMin, dataMax] = this.getDataMinandMax(lines, this.state.selectedComponents, this.state.selectedMetrics, this.state.selectedMonths);

    return (
      <div className={ASINChartStyle}>
        {this.state.compareStatus === OPERATION_STATUS.IN_PROGRESS && (
         <CompareMonthWindow onCancel={this.onCancelCompare} onSubmit={this.onSubmitCompare} exclude={this.getExcludedTimeFrames(data)}/>
        )}
        <div className="awsui-util-container">
          <div className="awsui-util-container-header">
            <div className="awsui-util-action-stripe">
              <div className="awsui-util-action-stripe-title">
                <div className="awsui-util-font-size-2">
                  { `${this.props.metricName} Trend` }
                </div>
              </div>
              <div className="awsui-util-action-stripe-group">
                <Button
                  text="Download"
                  variant="normal"
                  onClick={this.onDownloadData}
                  disabled={loading || empty}
                  loading={this.state.downloadStatus === OPERATION_STATUS.IN_PROGRESS}
                  data-cy="asin-trend-download"
                />
                {compareMonths && (<Button
                    text="Compare Months"
                    variant="normal"
                    onClick={this.onCompareMonth}
                    disabled={loading || empty}
                    loading={this.state.compareStatus === OPERATION_STATUS.IN_PROGRESS}
                    data-cy="compare-button"
                  />
                )}
              </div>
            </div>
          </div>
          {!loading && !empty && (
            <div>
              <ColumnLayout columns={3}>
                <div data-awsui-column-layout-root="true">
                  <div>
                    <TrendFilter
                      text="Component"
                      classname={AccuracyButtonNewStyle}
                      options={componentOpts}
                      onChange={this.setSelectedComponents}
                      selectedIds={this.state.selectedComponents}
                    />
                  </div>
                  <div>
                    <TrendFilter
                      text={this.props.metricName}
                      classname='AccuracyTypeMultiSelect'
                      options={TREND_CHART_DROPDOWN_ELEMENTS}
                      onChange={this.setSelectedMetrics}
                      selectedIds={this.state.selectedMetrics}
                    />
                  </div>
                  <div>
                    <TrendFilter
                      text="Month"
                      classname={AccuracyButtonNewStyle}
                      options={monthOpts}
                      onChange={this.setSelectedMonths}
                      selectedIds={this.state.selectedMonths}
                    />
                  </div>
                </div>
              </ColumnLayout>
              <div style={{ width: '99%', padding: '0' }}>
                <ResponsiveContainer aspect={3}>
                  <ComposedChart data={this.getFormattedChartData(data, displayInMillions)}
                                 margin={{ top: 10, right: 30, left: 40, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey='snapshot' label={{ value: 'As of Date', position: 'insideBottomLeft',
                      dx: -10, dy: 10}}
                           tickFormatter={YYYYMMDDToDate}/>
                    <YAxis type="number" domain={[dataMin, dataMax]} tickFormatter={(val) => val.toFixed(2)} >
                      <Label value='Amount ($)' position='insideLeft' angle={-90}/>
                    </YAxis>
                    <Tooltip />
                    <Legend />
                    {this.getLines(lines, this.state.selectedComponents, this.state.selectedMetrics, this.state.selectedMonths)}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>)}
          {loading && (<LoadingSpinner />)}
          {!loading && empty && (<EmptyMessage />)}
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/reports/asin_metrics/asin_trend_chart.jsx